// frontend/src/pages/MyDataPage.jsx
// Page "Mes données" — styles inline uniquement.
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BackButton from "../components/BackButton";
import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";

const s = {
  wrapper: {
    minHeight: "100vh",
    background: "#f9fafb",
    fontFamily: "system-ui, -apple-system, sans-serif",
    padding: "24px 16px 48px",
  },
  inner: {
    maxWidth: "480px",
    margin: "0 auto",
  },
  back: {
    display: "inline-block",
    fontSize: "13px",
    color: "#64748b",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 0 16px",
  },
  title: {
    fontSize: "22px",
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: "20px",
  },
  card: {
    background: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "16px",
    marginBottom: "12px",
  },
  cardTitle: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: "12px",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    fontSize: "13px",
    color: "#334155",
    padding: "5px 0",
    borderBottom: "1px solid #f8fafc",
    gap: "8px",
  },
  rowLabel: { color: "#94a3b8", flexShrink: 0 },
  rowValue: { textAlign: "right", wordBreak: "break-word" },
  btnPrimary: {
    width: "100%",
    padding: "12px",
    background: "#e11d48",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    marginBottom: "10px",
  },
  btnDanger: {
    width: "100%",
    padding: "12px",
    background: "#fee2e2",
    color: "#dc2626",
    border: "1px solid #fca5a5",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    marginBottom: "10px",
  },
  scanItem: {
    fontSize: "13px",
    color: "#334155",
    padding: "6px 0",
    borderBottom: "1px solid #f8fafc",
  },
  badge: (ok) => ({
    fontSize: "12px",
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: "99px",
    background: ok ? "#dcfce7" : "#fef9c3",
    color: ok ? "#166534" : "#854d0e",
  }),
};

export default function MyDataPage() {
  const navigate = useNavigate();
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  })();

  const [account, setAccount] = useState(null);
  const [scans, setScans] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const geolocEnabled = localStorage.getItem("geolocEnabled") === "true";
  const pushEnabled   = localStorage.getItem("pushEnabled") === "true" ||
                        storedUser?.subscribedToPush === true;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (!storedUser?.token) {
      navigate("/login");
      return;
    }
    const userId = storedUser._id || storedUser.id;
    const role   = storedUser.role;
    const headers = { Authorization: `Bearer ${storedUser.token}` };

    const scansUrl = role === "vendeur"
      ? `/api/scans/by-vendor/${userId}`
      : `/api/scans/history/${userId}`;

    Promise.all([
      fetch(`/api/auth/${userId}`, { headers }).then(r => r.json()),
      fetch(scansUrl, { headers }).then(r => r.json()),
      fetch("/api/stores/my", { headers }).then(r => r.json()),
    ])
      .then(([acc, scanData, storeData]) => {
        setAccount(acc);
        setScans(scanData?.scans || []);
        setStore(storeData?.store || null);
      })
      .catch(() => setError("Impossible de charger vos données."))
      .finally(() => setLoading(false));
  }, []);

  const handleExport = async () => {
    const payload = {
      compte: account,
      preferences: { geolocEnabled, pushEnabled },
      scans,
      magasin: store,
      exportedAt: new Date().toISOString(),
    };
    const jsonString = JSON.stringify(payload, null, 2);

    if (Capacitor.isNativePlatform()) {
      try {
        await Share.share({
          title: "Mes données CrushNow",
          text: jsonString,
          dialogTitle: "Exporter mes données",
        });
      } catch (err) {
        if (!String(err).toLowerCase().includes("cancel")) {
          console.error("Erreur export :", err);
        }
      }
    } else {
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "crushnow-mes-donnees.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const formatDate = (d) => d
    ? new Date(d).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })
    : "—";

  if (loading) return (
    <div style={{ ...s.wrapper, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "#94a3b8", fontSize: "14px" }}>Chargement…</p>
    </div>
  );

  if (error) return (
    <div style={{ ...s.wrapper, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "#dc2626", fontSize: "14px" }}>{error}</p>
    </div>
  );

  const role       = account?.role || storedUser?.role;
  const authMethod = account?.auth_method || (account?.email ? "email" : "pseudo");
  const since      = account?.registered_at || account?.createdAt;
  const displayedScans = scans.slice(0, 10);

  return (
    <div style={s.wrapper}>
      <div style={s.inner}>

        <BackButton />

        <h1 style={s.title}>🔍 Mes données</h1>

        {/* ── Section 1 — Compte ── */}
        <div style={s.card}>
          <p style={s.cardTitle}>👤 Mon compte</p>
          {[
            [authMethod === "email" ? "Email" : "Pseudonyme", account?.email || account?.username || storedUser?.name || "—"],
            ["Rôle", role === "vendeur" ? "Vendeur" : "Client"],
            ["Mode d'inscription", authMethod === "pseudo" ? "Sans email" : "Avec email"],
            ["Données démographiques", account?.gender ? `${account.gender}${account.ageRange ? ', ' + account.ageRange : ''}` : "Non renseignées"],
            ["Membre depuis", formatDate(since)],
          ].map(([label, value]) => (
            <div key={label} style={s.row}>
              <span style={s.rowLabel}>{label}</span>
              <span style={s.rowValue}>{value}</span>
            </div>
          ))}
        </div>

        {/* ── Section 2 — Préférences ── */}
        <div style={s.card}>
          <p style={s.cardTitle}>⚙️ Préférences (stockées sur cet appareil)</p>
          {[
            ...(role !== "vendeur" ? [["Géolocalisation", geolocEnabled ? "Activée" : "Désactivée"]] : []),
            ["Notifications", pushEnabled ? "Activées" : "Désactivées"],
          ].map(([label, value]) => (
            <div key={label} style={s.row}>
              <span style={s.rowLabel}>{label}</span>
              <span style={{ ...s.rowValue, ...s.badge(value.startsWith("A")) }}>{value}</span>
            </div>
          ))}
        </div>

        {/* ── Section 3 — Scans ── */}
        <div style={s.card}>
          <p style={s.cardTitle}>
            📦 {role === "vendeur" ? "Scans de mes produits" : "Mes scans"} ({scans.length} articles)
          </p>
          {displayedScans.length === 0 && (
            <p style={{ fontSize: "13px", color: "#94a3b8" }}>
              {role === "vendeur" ? "Aucun scan client enregistré." : "Aucun scan enregistré."}
            </p>
          )}
          {displayedScans.map((scan, i) => (
            <div key={scan._id || i} style={s.scanItem}>
              <span style={{ fontWeight: 600 }}>{scan.articleName || "Produit inconnu"}</span>
              <span style={{ color: "#94a3b8", marginLeft: "8px", fontSize: "12px" }}>
                {formatDate(scan.date)}
              </span>
              {scan.location_label && (
                <span style={{ display: "block", fontSize: "12px", color: "#64748b" }}>
                  🏪 {scan.location_label}
                </span>
              )}
            </div>
          ))}
          {scans.length > 10 && (
            <p style={{ fontSize: "12px", color: "#94a3b8", marginTop: "8px", textAlign: "center" }}>
              … et {scans.length - 10} autres scans
            </p>
          )}
        </div>

        {/* ── Section 4 — Magasin (vendeur uniquement) ── */}
        {role === "vendeur" && store && (
          <div style={s.card}>
            <p style={s.cardTitle}>🏪 Mon magasin</p>
            {[
              ["Nom",     store.name    || "—"],
              ["Adresse", store.address || "—"],
              ["SIRET",   store.siret   || "—"],
              ["Statut",  store.verified ? "Vérifié ✅" : "En attente ⏳"],
            ].map(([label, value]) => (
              <div key={label} style={s.row}>
                <span style={s.rowLabel}>{label}</span>
                <span style={s.rowValue}>{value}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Section 5 — Droits RGPD ── */}
        <div style={s.card}>
          <p style={s.cardTitle}>🔒 Vos droits RGPD</p>
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px", fontSize: "13px", color: "#334155", lineHeight: 2 }}>
            {role === "vendeur" ? (
              <>
                <li>• Vos données professionnelles ne sont jamais revendues à des tiers</li>
                <li>• Les statistiques de scan sont anonymisées — aucun client n'est identifié</li>
                <li>• Vos données boutique sont conservées tant que votre compte est actif</li>
                <li>• Votre compte est supprimé après 24 mois d'inactivité</li>
                <li>• Vous pouvez exporter ou supprimer vos données à tout moment</li>
              </>
            ) : (
              <>
                <li>• Tes données personnelles ne sont jamais vendues</li>
                <li>• Les magasins voient uniquement des stats anonymes</li>
                <li>• Tes scans sont conservés 12 mois</li>
                <li>• Ton compte est supprimé après 24 mois d'inactivité</li>
              </>
            )}
          </ul>

          <button style={s.btnPrimary} onClick={handleExport}>
            📤 Exporter mes données (JSON)
          </button>

          <p style={{ textAlign: "center", fontSize: "13px", color: "#64748b", margin: "8px 0 0" }}>
            📧{" "}
            <a href="mailto:support@crush-now.fr" style={{ color: "#e11d48" }}>
              Contacter support@crush-now.fr
            </a>
          </p>
        </div>

        <p style={{ textAlign: "center", fontSize: "11px", color: "#cbd5e1", marginTop: "24px" }}>
          CrushNow® v1.1.2
        </p>

      </div>
    </div>
  );
}
